import { trpc, RouterOutputs } from '@org/clients/school';
import {
  Badge,
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  unwrapTrpcData,
} from '@org/ui';
import { IDENTITY_STATUSES, SIGNUP_SOURCES, type IdentityStatus, type SignupSource } from '@org/model';
import { Link } from '@tanstack/react-router';
import { format } from 'date-fns';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { IdentityStatusChip } from '../identity/identity-review-list';
import { toDate, verdictPercent } from '../identity/identity-review-helpers';
import { getMutationErrorMessage } from '../shared/multi-step-utils';

type BaseForUser = RouterOutputs['access']['baseForUser'];
type IdentityInfo = NonNullable<BaseForUser['identity']>;

type StudentIdentityStepProps = {
  userId?: string;
  /** Current values from the student row (what the first step loaded). */
  defaultValues?: { identityStatus?: IdentityStatus | null; signupSource?: SignupSource | null; asaasCustomerId?: string | null };
  onSubmit: () => void;
  onClose: () => void;
  /** Selects differ from the saved values — lets the multi-step shell warn before leaving the step. */
  onDirtyChange?: (dirty: boolean) => void;
};

export interface StudentIdentityStepRef {
  submit: () => void;
}

const formatDate = (value: unknown) => {
  const date = toDate(value);
  return date ? format(date, 'PPp') : '';
};

/**
 * Third step of the student form: what the identity check found (read-only,
 * from the latest attempt) plus the two values an admin may set by hand —
 * identity status and sign-up source. The ASAAS customer id is shown, never edited.
 */
export const StudentIdentityStep = forwardRef<StudentIdentityStepRef, StudentIdentityStepProps>(function StudentIdentityStep(
  { userId, defaultValues, onSubmit, onClose, onDirtyChange },
  ref,
) {
  const { t } = useTranslation('admin');
  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.access.baseForUser.useQuery({ userId: userId ?? '' }, { enabled: !!userId });
  const info = unwrapTrpcData<BaseForUser>(data as BaseForUser) ?? null;
  const identity: IdentityInfo | null = info?.identity ?? null;

  const [identityStatus, setIdentityStatus] = useState<IdentityStatus>(defaultValues?.identityStatus ?? 'verified');
  const [signupSource, setSignupSource] = useState<SignupSource>(defaultValues?.signupSource ?? 'invite');

  // The row's values win over the step defaults once they arrive.
  useEffect(() => {
    if (identity?.status) setIdentityStatus(identity.status);
    if (identity?.signupSource) setSignupSource(identity.signupSource);
  }, [identity?.status, identity?.signupSource]);

  // "Saved" = what the server holds (or the row defaults until it answers).
  const savedStatus = identity?.status ?? defaultValues?.identityStatus ?? 'verified';
  const savedSource = identity?.signupSource ?? defaultValues?.signupSource ?? 'invite';
  const dirty = identityStatus !== savedStatus || signupSource !== savedSource;
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const updateMutation = trpc.users.update.useMutation({
    onError: (error: unknown) =>
      toast.error(t('students.identityStep.toasts.error', { message: getMutationErrorMessage(error) })),
  });

  const handleSave = async () => {
    if (!userId) {
      onClose();
      return;
    }
    try {
      await updateMutation.mutateAsync({ id: userId, data: { identityStatus, signupSource } });
      toast.success(t('common.toasts.updateSuccess', { entity: 'User' }));
      await Promise.all([
        utils.users.list.invalidate(),
        utils.users.listStudentsWithDetails.invalidate(),
        utils.access.baseForUser.invalidate({ userId }),
        utils.identity.listForReview.invalidate(),
      ]);
      onSubmit();
    } catch {
      // onError already reported it.
    }
  };

  useImperativeHandle(ref, () => ({ submit: () => void handleSave() }));

  const verdictLine = (row: IdentityInfo) => {
    if (!row.aiVerdict) return t('identityReviews.verdict.none');
    const pct = verdictPercent(row.aiVerdict);
    return t(`identityReviews.verdict.${row.aiVerdict.verdict}`, { percent: pct ?? 0 });
  };

  const needsHuman = identity?.status === 'needs_review' || identity?.status === 'declined';

  return (
    <div className="flex flex-col gap-6">
      {/* What the check found — read only */}
      <section className="rounded-md border p-4">
        <h3 className="mb-3 text-sm font-medium">{t('students.identityStep.checkTitle')}</h3>
        {!userId ? (
          <p className="text-sm text-muted-foreground">{t('students.identityStep.noPhoto')}</p>
        ) : isLoading ? (
          <div className="flex gap-4">
            <Skeleton className="h-40 w-40 rounded-md" />
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-64" />
              <Skeleton className="h-4 w-48" />
            </div>
          </div>
        ) : !identity?.photoUrl ? (
          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <span>{t('students.identityStep.noPhoto')}</span>
            {identity ? <IdentityStatusChip status={identity.status} /> : null}
          </div>
        ) : (
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="aspect-square w-40 shrink-0 overflow-hidden rounded-md bg-muted">
              <img src={identity.photoUrl} alt={t('students.identityStep.photoAlt')} className="h-full w-full object-cover" />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-2 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <IdentityStatusChip status={identity.status} />
                {identity.photoKind && (
                  <Badge variant="outline">{t(`identityReviews.photoKind.${identity.photoKind}`)}</Badge>
                )}
              </div>
              <span>{verdictLine(identity)}</span>
              {identity.aiVerdict?.reason ? (
                <span className="text-xs italic text-muted-foreground">“{identity.aiVerdict.reason}”</span>
              ) : null}
              <span className="text-xs text-muted-foreground">
                {t('identityReviews.attempts', { count: identity.attempts })}
                {identity.checkedAt ? ` · ${t('identityReviews.checkedAt', { date: formatDate(identity.checkedAt) })}` : ''}
                {identity.reviewedAt ? ` · ${t('students.identityStep.reviewedAt', { date: formatDate(identity.reviewedAt) })}` : ''}
              </span>
              {needsHuman && (
                <Link to="/identity-reviews" className="text-xs underline text-rose-700">
                  {t('baseAccess.reviewLink')}
                </Link>
              )}
            </div>
          </div>
        )}
      </section>

      {/* What an admin may set by hand */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <Label htmlFor="identity-status">{t('students.identityStep.labels.identityStatus')}</Label>
          <Select value={identityStatus} onValueChange={(v) => setIdentityStatus(v as IdentityStatus)}>
            <SelectTrigger id="identity-status" className="w-full text-left">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {IDENTITY_STATUSES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`students.identityStep.status.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">{t('students.identityStep.hints.identityStatus')}</p>
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="signup-source">{t('students.identityStep.labels.signupSource')}</Label>
          <Select value={signupSource} onValueChange={(v) => setSignupSource(v as SignupSource)}>
            <SelectTrigger id="signup-source" className="w-full text-left">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SIGNUP_SOURCES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`students.identityStep.source.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">{t('students.identityStep.hints.signupSource')}</p>
        </div>
        {defaultValues?.asaasCustomerId ? (
          <div className="flex flex-col gap-1 sm:col-span-2">
            <Label>{t('students.identityStep.labels.asaasCustomerId')}</Label>
            <code className="w-fit rounded bg-muted px-2 py-1 text-xs">{defaultValues.asaasCustomerId}</code>
          </div>
        ) : null}
      </div>

      <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onClose} disabled={updateMutation.isPending}>
          {t('common.actions.cancel')}
        </Button>
        <Button type="button" variant="outline" onClick={handleSave} disabled={updateMutation.isPending}>
          {updateMutation.isPending ? t('common.status.saving') : t('common.actions.update')}
        </Button>
      </div>
    </div>
  );
});
